import React, { useState, useEffect, useContext } from 'react';
import { CityContext } from '../contexts/CityContext';
import '../styles/productFilters.css';

const ProductFilters = ({ 
  filters, 
  onFiltersChange, 
  productsCount, 
  totalProducts 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [localFilters, setLocalFilters] = useState(filters);

  // Get cities from context
  const { cities, isLoading: citiesLoading } = useContext(CityContext);

  // Sync local filters with parent when filters prop changes
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  // Handle individual filter changes
  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...localFilters, [filterType]: value };
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  // Reset all filters
  const resetFilters = () => {
    const resetFilters = {
      sortBy: '',
      condition: '',
      minPrice: '',
      maxPrice: '',
      city: ''
    };
    setLocalFilters(resetFilters);
    onFiltersChange(resetFilters);
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    return Object.values(localFilters).some(value => {
      if (typeof value === 'boolean') return value;
      if (typeof value === 'string') return value.trim() !== '';
      return false;
    });
  };

  const activeFilterCount = () => {
    let count = 0;
    Object.entries(localFilters).forEach(([key, value]) => {
      if (key === 'sortBy') return; // Don't count sort as a filter
      if (key === 'negotiable' || key === 'promotedOnly') return; // Skip removed filters
      if (typeof value === 'boolean' && value) count++;
      if (typeof value === 'string' && value.trim() !== '') count++;
    });
    return count;
  };

  return (
    <div className="product-filters">
      {/* Filter Header */}
      <div className="filters-header">
        <div className="filters-info">
          <h3 className="filters-title">
            <i className="fas fa-filter"></i>
            Filtres
            {hasActiveFilters() && (
              <span className="active-filters-count">({activeFilterCount()})</span>
            )}
          </h3>
          <div className="results-info">
            {productsCount} sur {totalProducts} produit{totalProducts > 1 ? 's' : ''}
            {productsCount !== totalProducts && (
              <span className="filtered-indicator">
                <i className="fas fa-filter" style={{ color: '#003366' }}></i>
                Filtré
              </span>
            )}
          </div>
        </div>
        
        <div className="filters-controls">
          {hasActiveFilters() && (
            <button 
              className="reset-filters-btn"
              onClick={resetFilters}
              title="Réinitialiser les filtres"
            >
              <i className="fas fa-times"></i>
              Réinitialiser
            </button>
          )}
          <button
            className="toggle-filters-btn"
            onClick={() => setIsExpanded(!isExpanded)}
            aria-label={isExpanded ? 'Masquer les filtres' : 'Afficher les filtres'}
          >
            <i className={`fas ${isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
            {isExpanded ? 'Masquer' : 'Afficher'}
          </button>
        </div>
      </div>

      {/* Filter Options */}
      <div className={`filters-content ${isExpanded ? 'expanded' : ''}`}>
        <div className="filters-grid">
          {/* Sort Filter */}
          <div className="filter-group">
            <label className="filter-label">
              <i className="fas fa-sort"></i>
              Trier par
            </label>
            <select
              className="filter-select"
              value={localFilters.sortBy}
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
            >
              <option value="">Par défaut</option>
              <option value="newest">Plus récent</option>
              <option value="oldest">Plus ancien</option>
              <option value="price_low">Prix croissant</option>
              <option value="price_high">Prix décroissant</option>
              <option value="most_liked">Plus aimé</option>
              <option value="most_viewed">Plus vues</option>
            </select>
          </div>

          {/* Condition Filter */}
          <div className="filter-group">
            <label className="filter-label">
              <i className="fas fa-star"></i>
              État
            </label>
            <select
              className="filter-select"
              value={localFilters.condition}
              onChange={(e) => handleFilterChange('condition', e.target.value)}
            >
              <option value="">Tous les états</option>
              <option value="Nouveau">Nouveau</option>
              <option value="Usagé">Usagé</option>
            </select>
          </div>

          {/* Price Range */}
          <div className="filter-group price-range-group">
            <label className="filter-label">
              <i className="fas fa-dollar-sign"></i>
              Prix
            </label>
            <div className="price-inputs">
              <input
                type="number"
                className="filter-input price-input"
                placeholder="Min"
                value={localFilters.minPrice}
                onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                min="0"
              />
              <span className="price-separator">-</span>
              <input
                type="number"
                className="filter-input price-input"
                placeholder="Max"
                value={localFilters.maxPrice}
                onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                min="0"
              />
            </div>
          </div>

          {/* City Filter */}
          <div className="filter-group">
            <label className="filter-label">
              <i className="fas fa-map-marker-alt"></i>
              Ville
            </label>
            {citiesLoading ? (
              <div className="filter-select" style={{ display: 'flex', alignItems: 'center', color: '#666' }}>
                <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                Chargement des villes...
              </div>
            ) : (
              <select
                className="filter-select"
                value={localFilters.city}
                onChange={(e) => handleFilterChange('city', e.target.value)}
              >
                <option value="">Toutes les villes</option>
                {cities && cities.length > 0 ? (
                  cities.map(city => (
                    <option key={city.id} value={city.name}>
                      {city.name}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>Aucune ville disponible</option>
                )}
              </select>
            )}
          </div>
        </div>

        {/* Quick Filter Buttons */}
        <div className="quick-filters">
          <div className="quick-filters-label">Filtres rapides:</div>
          <div className="quick-filter-buttons">
            <button
              className={`quick-filter-btn ${localFilters.condition === 'Nouveau' ? 'active' : ''}`}
              onClick={() => handleFilterChange('condition', localFilters.condition === 'Nouveau' ? '' : 'Nouveau')}
            >
              <i className="fas fa-gem"></i>
              Nouveau
            </button>
            <button
              className={`quick-filter-btn ${localFilters.sortBy === 'price_low' ? 'active' : ''}`}
              onClick={() => handleFilterChange('sortBy', localFilters.sortBy === 'price_low' ? '' : 'price_low')}
            >
              <i className="fas fa-arrow-up"></i>
              Prix ↑
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductFilters;
